import * as semver from 'semver';
import {
  type LibraryVersionedResource,
  type LoadedLibraries,
  LibraryManifest,
  LIBRARY_STATUS,
  libraryManifestFromResource,
  LIBRARY_SOURCE,
  LIBRARY_PROFILE_STATUS,
  isLibraryOrigin,
  getLibraryCapabilities,
  LibrariesInstallationType,
  LIBRARY_ACTION,
  PREBUILT_LAYOUTS_PLUGIN_ID,
} from '@packages/systems/libraries';
import {qualify, getName, namesEqual} from '@packages/systems/wfdl-Qualified';
import {Asset, Component, Plugins} from '@packages/systems/wfdl';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  getComponentByOrigin,
  getPageComponentRootElement,
  getPageComponentFromComponentMap,
  visitNativeIdPathSymbolInstances,
  getComponentInstOrigin,
} from '@packages/utilities/platform';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {AssetState} from '@packages/systems/designer/assets';
import {Locale} from '@packages/systems/localization-types';
import {reportBugOnce} from '@packages/utilities/reporter';
import type {DesignerSourceFile} from '@packages/domains/code-libraries/data-types';
import type {AgentInstructionLibraryMetadata} from '@packages/domains/instructions/data-types';
import {agentSkillConstraints} from '@packages/domains/instructions/data-types';
import {ManageLibraryState} from './constants';

/** Function to parse the flat list of available libraries from our API
 * and returns identified list of
 * - Libraries that are available on the current site
 * - Loaded libraries that are installed on the current site, indicating if update is available
 */
export function parseAvailableLibraries(
  ongoingLibraryInstallation: LibrariesInstallationType,
  loadedLibraries: LoadedLibraries,
  allAvailableLibraries: LibraryVersionedResource[],
  allLibrariesFetched: boolean = false
): Array<LibraryManifest<string>> {
  if (Object.keys(loadedLibraries).length === 0) {
    return emptyArray;
  }

  // Build a map of profileIds returned by the latest API fetch. After a successful
  // fetch, any installed library whose profileId is absent from this set has been unshared.
  const availableProfileIds = new Set(
    allAvailableLibraries.map((l) => l.profileId)
  );

  // Indicate in our loaded libraries if an update is available
  const {installedLibraries, installedLibrariesLookup} = Object.entries(
    loadedLibraries
  ).reduce<{
    installedLibraries: Array<LibraryManifest<string>>;
    installedLibrariesLookup: {[profileId: string]: boolean};
  }>(
    (collector, [libraryPluginId, loadedLibrary]) => {
      // We only want to collect Workspace libraries here,
      // at least until we consolidate Workspace and Marketplace libraries
      if (loadedLibrary.source !== LIBRARY_SOURCE.WORKSPACE) {
        return collector;
      }

      // Hide libraries installed by AI
      if (getLibraryCapabilities(loadedLibrary).isHidden) {
        return collector;
      }

      // We don't want the pre-installed layouts so filter them out
      if (libraryPluginId === PREBUILT_LAYOUTS_PLUGIN_ID) {
        return collector;
      }

      const updatedLibraryVersion = findUpdatedLibraryVersion(
        allAvailableLibraries,
        loadedLibrary
      );

      // After a successful fetch, if the library is missing from the available set
      // and has no newer version, it was unshared on the source site.
      const inferredUnshared =
        allLibrariesFetched &&
        !updatedLibraryVersion &&
        !availableProfileIds.has(loadedLibrary.profileId);

      // Add this library manifest with the proper status and corresponding updated library version
      collector.installedLibraries.push({
        ...loadedLibrary,
        status:
          // If there's an update available for this loaded library, we'll indicate that
          updatedLibraryVersion
            ? LIBRARY_STATUS.upgrade_available
            : // If this loaded library was just installed, use that installation status
              ongoingLibraryInstallation[libraryPluginId]?.installationStatus ||
              // Otherwise, it's a loaded library, so by default its status is installed
              LIBRARY_STATUS.installed,
        // When the fetch is complete, derive profile status from allAvailableLibraries
        // presence (the server only returns ACTIVE libraries, so absence is the
        // authoritative UNSHARED signal). This avoids trusting the versioned resource
        // payload, which can carry a stale UNSHARED status after a reshare+publish cycle.
        // When the fetch hasn't completed (or has failed), fall back to the previously
        // known status so a library that was already marked UNSHARED keeps its badge.
        profileStatus: allLibrariesFetched
          ? inferredUnshared
            ? LIBRARY_PROFILE_STATUS.UNSHARED
            : LIBRARY_PROFILE_STATUS.SHARED
          : updatedLibraryVersion
            ? updatedLibraryVersion.profileStatus
            : loadedLibrary.profileStatus,
        updatedLibraryVersion,
      });
      collector.installedLibrariesLookup[loadedLibrary.profileId] = true;

      return collector;
    },
    {installedLibraries: [], installedLibrariesLookup: {}}
  );

  // Non-installed libraries are those libraries not present in our loaded libraries
  const notInstalled = allAvailableLibraries.reduce<
    Array<LibraryManifest<string>>
  >((collector, availableLibrary) => {
    // We only want not-installed and shared libraries in this list
    if (
      installedLibrariesLookup[availableLibrary.profileId] ||
      availableLibrary.profileStatus === LIBRARY_PROFILE_STATUS.UNSHARED
    ) {
      return collector;
    }

    // Add this library manifest created from the available library
    collector.push({
      ...libraryManifestFromResource(availableLibrary, {
        status:
          ongoingLibraryInstallation[availableLibrary.id]?.installationStatus ||
          LIBRARY_STATUS.install_available,
      }),

      updatedLibraryVersion: undefined,
    });
    return collector;
  }, []);

  return (
    installedLibraries
      .concat(notInstalled)
      // Sort the libraries by name to maintain consistent order
      .sort((libManifestA, libManifestB) => {
        if (libManifestA.displayName < libManifestB.displayName) {
          return -1;
        }
        if (libManifestA.displayName > libManifestB.displayName) {
          return 1;
        }
        return 0;
      })
  );
}

/** Given a list of `LibraryVersionedResource` objects - like the ones we receive
 * from our backend API server for the list of available libraries for installation,
 * this function takes a `LibraryManifest` object - i.e. installed library - and attempts
 * to find if there's an updated version of that `LibraryManifest` object from the list
 * of available libraries.
 *
 * Returns a `LibraryManifest` object constructed from the matching available library
 * if an updated available library was found, and it was determined to be an update
 * based on the version string.
 *
 * Otherwise, returns null
 */
function findUpdatedLibraryVersion(
  availableLibraries: Array<LibraryVersionedResource>,
  currentLibrary: LibraryManifest<string>
): LibraryManifest<string> | undefined {
  const matchingAvailableLibrary = availableLibraries.find(
    (availableLibrary) =>
      availableLibrary.profileId === currentLibrary.profileId
  );

  // No matching library from the available library set was found
  if (!matchingAvailableLibrary) {
    return undefined;
  }

  // Check if there's an update for our loaded library. If the version of our current library
  // is less than the available library, then there's an update available
  const libraryUpdateAvailable = semver.lt(
    currentLibrary.version,
    matchingAvailableLibrary.version
  );

  // The matching available library isn't newer than our current
  if (!libraryUpdateAvailable) {
    return undefined;
  }

  // Otherwise, this matching available library is an updated version
  // of our current library. Let's return it as a LibraryManifest
  return libraryManifestFromResource(matchingAvailableLibrary);
}

export type LibraryItemsInUse = {
  componentsInUse: Set<string>;
  variablesInUse: Set<string>;
  assetsInUse: Set<string>;
};

export function getLibraryItemsInUse({
  pluginId,
  componentInstanceCounts,
  components,
  assets,
  plugins,
}: {
  pluginId: string;
  componentInstanceCounts: Record<string, number>;
  components: Component.ComponentMap;
  assets: AssetState['assets'];
  plugins: Plugins.PluginsType;
}): LibraryItemsInUse {
  // Iterate through the components of the library and find if it's used in the current site
  const pluginComponents = Plugins.getPluginComponents(plugins).byPlugin();
  const libraryComponents = pluginComponents[pluginId] || {};
  const componentsInUse = Object.entries(libraryComponents).reduce<Set<string>>(
    (collector, [componentId]) => {
      // Library component origin
      const origin = qualify(
        Plugins.name(pluginId),
        Component.name(componentId)
      );

      // Get the local component from map
      const {componentId: localComponentId} = getComponentByOrigin(
        origin,
        components
      );

      // No local component found for this origin - because it has never been dragged and
      // dropped into this site. This indicates the component is not in use
      if (!localComponentId) {
        return collector;
      }

      const componentInstanceCount: number =
        componentInstanceCounts[getName(localComponentId)] || 0;

      // We only want components where the instance count is more than 0
      if (componentInstanceCount > 0) {
        collector.add(componentId);
      }

      return collector;
    },
    new Set<string>()
  );

  // TODO - Implement counting variables used from this library
  // https://webflow.atlassian.net/browse/SCALE-187
  const variablesInUse = new Set<string>();

  // Iterate through the assets of the library and find if it's imported in the current site
  const pluginAssets = Plugins.getPluginAssets(plugins).byPlugin();
  const libraryAssets = pluginAssets[pluginId] || {};
  const assetsInUse = Object.entries(libraryAssets).reduce<Set<string>>(
    (collector, [assetId]) => {
      // Library asset origin
      const origin = qualify(Plugins.name(pluginId), Asset.name(assetId));

      const localAsset = assets.find((asset) => {
        if (!asset.origin) {
          return false;
        }
        return namesEqual(asset.origin, origin);
      });

      // We only want assets that were imported
      if (localAsset) {
        collector.add(assetId);
      }

      return collector;
    },
    new Set<string>()
  );

  return {componentsInUse, variablesInUse, assetsInUse};
}

/**
 * Given the loaded libraries, component map and component instance
 * Returns if the component instance originates from a library.
 */
export function isComponentInstanceFromLibrary({
  libraries,
  componentMap,
  componentInstance,
}: {
  libraries: LoadedLibraries;
  componentMap: Component.ComponentMap;
  componentInstance: ExpressionElement;
}): boolean {
  const origin = getComponentInstOrigin(componentMap)(componentInstance);
  return isLibraryOrigin(origin, libraries);
}

/**
 * Given the loaded libraries, component map and native id path
 * Returns if the component instance originates from a library.
 */
export function isComponentInstanceNativeIdPathFromLibrary({
  libraries,
  componentMap,
  nativeIdPath,
}: {
  libraries: LoadedLibraries;
  componentMap: Component.ComponentMap;
  nativeIdPath: NativeId[];
}): boolean {
  const rootElement = getPageComponentRootElement(componentMap);
  let componentType = getPageComponentFromComponentMap(componentMap);
  if (!rootElement || nativeIdPath.length === 0) {
    return false;
  }
  let result = false;
  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    rootElement,
    componentMap,
    (componentInstance) => {
      if (
        componentType &&
        !result &&
        isComponentInstanceFromLibrary({
          libraries,
          componentMap,
          componentInstance,
        })
      ) {
        result = true;
      }
      componentType = componentMap.get(componentInstance.type);
    }
  );
  return result;
}

/**
 * Returns a key for the LibraryOrphanedObjectsManager content.
 *
 * Returns 'main' for null branchId, or appends locale tag for non-primary locales.
 */
export function getLibraryOrphanedObjectsKey({
  branchId,
  currentLocale,
}: {
  branchId: string | null;
  currentLocale: Locale | undefined;
}): string {
  let key = branchId || 'main';
  if (currentLocale && !currentLocale.primary) {
    key = `${key}-${currentLocale.tag}`;
  }
  return key;
}

/**
 * Based on the installations state, returns the current ongoing manage library action
 */
export const getManageLibraryActionFromInstallations = (
  installations: LibrariesInstallationType
): ManageLibraryState | null => {
  let found: ManageLibraryState | null = null;

  const reportMultipleActionsRunning = () => {
    reportBugOnce(
      '[Libraries][getManageLibraryActionFromInstallations]',
      'Multiple libraries actions are running.',
      {
        team: 'structure',
        ticket: 'https://webflow.atlassian.net/browse/STRUCT-2932',
        installations,
      }
    );
  };

  for (const [versionId, installation] of Object.entries(installations)) {
    const {installationStatus, profileId} = installation;

    if (installationStatus === LIBRARY_STATUS.installing) {
      if (found) {
        reportMultipleActionsRunning();
        return found;
      }

      found = {
        action: LIBRARY_ACTION.INSTALL,
        status: 'pending',
        libraryResourceId: versionId,
        libraryProfileId: profileId,
      };
    }

    if (installationStatus === LIBRARY_STATUS.uninstalling) {
      if (found) {
        reportMultipleActionsRunning();
        return found;
      }

      found = {
        action: LIBRARY_ACTION.UNINSTALL,
        status: 'pending',
        libraryResourceId: versionId,
        libraryProfileId: profileId,
      };
    }
  }

  return found;
};

const emptyLibraryMetadata: AgentInstructionLibraryMetadata = {
  sourceFilesCount: 0,
  skillsCount: 0,
  rulesCount: 0,
};

/** Derives agent-instruction (rule/skill) counts from SourceFilesStore's records. */
export function getLibraryMetadataFromSourceFiles(
  sourceFiles: DesignerSourceFile[]
): AgentInstructionLibraryMetadata {
  let sourceFilesCount = 0;
  let skillsCount = 0;
  let rulesCount = 0;

  for (const sourceFile of sourceFiles) {
    if (sourceFile.origin !== undefined) continue;

    if (sourceFile.kind === 'rule') {
      if (sourceFile.isDraft) continue;
      sourceFilesCount += 1;
      rulesCount += 1;
    } else if (sourceFile.kind === 'skill') {
      const isSkillRoot = sourceFile.name === agentSkillConstraints.fileName;
      // Source files carry no folder ancestry to pair a companion with its
      // SKILL.md, so only SKILL.md's own draft flag can exclude a skill file.
      if (isSkillRoot && sourceFile.isDraft) continue;
      sourceFilesCount += 1;
      if (isSkillRoot && sourceFile.folderId != null) {
        skillsCount += 1;
      }
    }
  }

  if (sourceFilesCount === 0) {
    return emptyLibraryMetadata;
  }

  return {sourceFilesCount, skillsCount, rulesCount};
}
